import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { AuditTrailViewer } from '../../components/AuditTrail';
import DeleteRecordButton, { deleteNeedsReason } from '../../components/DeleteRecordButton';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { formatDate } from '../../utils/dateUtils';
import { capaSourceLabel, capaTypeLabel, capaAssigneeLabel } from '../../utils/capaLabels';
import TabNavigation from '../../components/TabNavigation';
import FileUploadWidget from '../../components/FileUploadWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAPADetail = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [capa, setCapa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [comments, setComments] = useState('');
  // CMP-25: verifier records Effective or Not effective (the API already
  // supported is_effective=false; the dialog always sent true).
  const [isEffective, setIsEffective] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedCapa, setEditedCapa] = useState(null);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  // S2-24: evidence files attached to this CAPA (file_uploads module='capa').
  // The server refuses submit-for-verification / verify / close while a CAPA
  // marked "Evidence required" has none.
  const [evidenceFiles, setEvidenceFiles] = useState([]);

  useEffect(() => {
    fetchCAPA();
    fetchEvidence();
  }, [id]);

  const fetchEvidence = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/files`, {
        params: { module: 'capa', reference_id: id },
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvidenceFiles((res.data.files || []).map(f => ({
        id: f.id,
        filename: f.original_filename || f.filename,
        content_type: f.content_type,
        file_size: f.file_size,
        uploaded_by: f.uploaded_by_name,
      })));
    } catch (err) {
      setEvidenceFiles([]);
    }
  };

  // FileUploadWidget reports the new list; a file missing from it was removed
  // by the user, so soft-delete it server-side (audited in file_access_logs).
  const handleEvidenceChange = async (nextFiles) => {
    const nextIds = new Set(nextFiles.map(f => f.id));
    const removed = evidenceFiles.filter(f => !nextIds.has(f.id));
    setEvidenceFiles(nextFiles);
    if (removed.length === 0) return;
    const token = localStorage.getItem('token');
    try {
      await Promise.all(removed.map(f => axios.delete(`${API}/files/${f.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })));
    } catch (err) {
      showError('Failed to remove evidence file: ' + (err.response?.data?.detail || err.message));
    }
    await fetchEvidence();
  };

  const fetchCAPA = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/capa/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapa(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load CAPA');
      setLoading(false);
    }
  };

  // CMP-07: status changes go through the state-checked workflow endpoints.
  // The edit endpoint (PUT /capa/{id}) no longer accepts a status change.
  const STATUS_ACTION_PATHS = {
    'In Progress': 'start',
    'Pending Verification': 'submit-for-verification',
  };

  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const actionPath = STATUS_ACTION_PATHS[newStatus];
      if (!actionPath) {
        throw new Error(`No workflow action for status ${newStatus}`);
      }
      await axios.post(`${API}/capa/${id}/${actionPath}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Refresh CAPA data
      await fetchCAPA();
      showSuccess(`CAPA status updated to: ${newStatus}`);
    } catch (err) {
      showError('Failed to update status: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!comments.trim()) {
      showWarning('Please enter verification comments');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/capa/${id}/verify`,
        { is_effective: isEffective, comments },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setShowVerifyModal(false);
      setComments('');
      setIsEffective(true);
      await fetchCAPA();
      showSuccess(isEffective
        ? 'CAPA verified as effective and closed'
        : 'CAPA recorded as not effective and returned to In Progress');
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail))
        : err.message;
      showError('Failed to verify CAPA: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      showWarning('Please enter rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/capa/${id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setShowRejectModal(false);
      setRejectionReason('');
      await fetchCAPA();
      showSuccess('CAPA rejected - rework required');
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail))
        : err.message;
      showError('Failed to reject CAPA: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/capa/${id}/submit`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCAPA();
      showSuccess('CAPA submitted for approval');
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail))
        : err.message;
      showError('Failed to submit CAPA: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/capa/${id}/approve`,
        { comments: approvalComments },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowApproveModal(false);
      setApprovalComments('');
      await fetchCAPA();
      showSuccess('CAPA approved successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail))
        : err.message;
      showError('Failed to approve CAPA: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseCapa = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/capa/${id}/close`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCAPA();
      showSuccess('CAPA closed successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail))
        : err.message;
      showError('Failed to close CAPA: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedCapa({
      ...capa,
      due_date: capa.due_date ? capa.due_date.split('T')[0] : ''
    });
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/capa/${id}`, editedCapa, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsEditing(false);
      await fetchCAPA();
      showSuccess('CAPA updated successfully!');
    } catch (err) {
      showError('Failed to update CAPA: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedCapa(null);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Assigned': 'bg-blue-100 text-blue-800',
      'Pending Approval': 'bg-orange-100 text-orange-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Pending Verification': 'bg-purple-100 text-purple-800',
      'Verified': 'bg-green-100 text-green-800',
      'Closed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Button variant="outline" onClick={() => navigate('/capa')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to CAPA List
        </Button>
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <Button variant="outline" onClick={() => navigate('/capa')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to CAPA List
      </Button>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Header Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-2xl mb-2">{capa.title}</CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-xs px-3 py-1 rounded-full ${getSeverityColor(capa.severity)}`}>
                    {capa.severity}
                  </span>
                  <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(capa.status)}`}>
                    {capa.status}
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-800">
                    {capaTypeLabel(capa.capa_type)}
                  </span>
                </div>
              </div>
              {/* Soft delete — any status (reason required unless Draft);
                  backend enforces capa:delete + site access + the reason rule. */}
              {hasPermission('capa', 'delete') && !capa.read_only && (
                <DeleteRecordButton
                  url={`${API}/capa/${capa.id}`}
                  label={`CAPA ${capa.capa_number || ''}`.trim()}
                  requireReason={deleteNeedsReason(capa.status)}
                  onDeleted={() => navigate('/capa')}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* QA #88: corrective and preventive actions are stored separately
                (capas.corrective_actions / preventive_actions). They used to be
                fused into `description` at write time by
                routes/hira.py create_capa_from_hazard, which is why opening a
                HIRA-sourced CAPA here showed one combined Description.
                Legacy rows still have only the fused text, so `description` is
                the fallback rather than a duplicate. */}
            {(capa.corrective_actions || capa.preventive_actions) ? (
              <div className="space-y-4">
                {capa.corrective_actions && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Corrective Action</label>
                    <p className="text-gray-700 mt-1 whitespace-pre-line">{capa.corrective_actions}</p>
                  </div>
                )}
                {capa.preventive_actions && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Preventive Action</label>
                    <p className="text-gray-700 mt-1 whitespace-pre-line">{capa.preventive_actions}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-700 whitespace-pre-line">{capa.description}</p>
            )}
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>CAPA Details</CardTitle>
              {!isEditing && capa.status !== 'Closed' && hasPermission('capa', 'update') && (
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              // Edit Mode
              <>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Title</label>
                  <input
                    type="text"
                    value={editedCapa.title}
                    onChange={(e) => setEditedCapa({...editedCapa, title: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Description</label>
                  <textarea
                    value={editedCapa.description}
                    onChange={(e) => setEditedCapa({...editedCapa, description: e.target.value})}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                {/* QA #88: editable here too, so the split survives the whole CAPA
                    lifecycle rather than only existing at creation. PUT /api/capa
                    accepts both keys via the field allowlist in routes/capa.py. */}
                <div>
                  <label className="text-sm font-semibold text-gray-600">Corrective Action</label>
                  <textarea
                    value={editedCapa.corrective_actions || ''}
                    onChange={(e) => setEditedCapa({...editedCapa, corrective_actions: e.target.value})}
                    rows={3}
                    placeholder="Action taken to address the problem that occurred"
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Preventive Action</label>
                  <textarea
                    value={editedCapa.preventive_actions || ''}
                    onChange={(e) => setEditedCapa({...editedCapa, preventive_actions: e.target.value})}
                    rows={3}
                    placeholder="Action taken to stop it recurring"
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Priority</label>
                    <select
                      value={editedCapa.priority}
                      onChange={(e) => setEditedCapa({...editedCapa, priority: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  {/* REL-14: severity is stored on its own, independent of priority. */}
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Severity</label>
                    <select
                      value={editedCapa.severity || ''}
                      onChange={(e) => setEditedCapa({...editedCapa, severity: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Due Date</label>
                    <input
                      type="date"
                      value={editedCapa.due_date}
                      onChange={(e) => setEditedCapa({...editedCapa, due_date: e.target.value})}
                      min={new Date().toISOString().split("T")[0]}
                      max="2099-12-31"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Root Cause</label>
                  <textarea
                    value={editedCapa.root_cause || ''}
                    onChange={(e) => setEditedCapa({...editedCapa, root_cause: e.target.value})}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                {/* S2-24: closure requirements. The server lets a requirement be
                    added at any time but removed only while the CAPA is a Draft. */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-600">Closure Requirements</label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!editedCapa.evidence_required}
                      disabled={capa.status !== 'Draft' && !!capa.evidence_required}
                      onChange={(e) => setEditedCapa({...editedCapa, evidence_required: e.target.checked})}
                    />
                    Evidence required to close
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!editedCapa.effectiveness_review_required}
                      disabled={capa.status !== 'Draft' && !!capa.effectiveness_review_required}
                      onChange={(e) => setEditedCapa({...editedCapa, effectiveness_review_required: e.target.checked})}
                    />
                    Effectiveness review required
                  </label>
                  {capa.status !== 'Draft' && (capa.evidence_required || capa.effectiveness_review_required) && (
                    <p className="text-xs text-gray-500">
                      Requirements can only be removed while the CAPA is a Draft.
                    </p>
                  )}
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveEdit} disabled={actionLoading}>
                    {actionLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={actionLoading}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              // View Mode
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Source</label>
                    <p className="text-gray-900">{capaSourceLabel(capa.source)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Severity</label>
                    <p className="text-gray-900">
                      <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(capa.severity)}`}>
                        {capa.severity}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Priority</label>
                    <p className="text-gray-900 capitalize">{capa.priority}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Assigned To</label>
                    <p className="text-gray-900">{capaAssigneeLabel(capa)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Due Date</label>
                    <p className="text-gray-900">{capa.due_date ? formatDate(capa.due_date) : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Created By</label>
                    <p className="text-gray-900">{capa.created_by_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Created Date</label>
                    <p className="text-gray-900">{formatDate(capa.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Evidence Required to Close</label>
                    <p className="text-gray-900">{capa.evidence_required ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Effectiveness Review Required</label>
                    <p className="text-gray-900">{capa.effectiveness_review_required ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                {capa.root_cause && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Root Cause</label>
                    <p className="text-gray-900 mt-1">{capa.root_cause}</p>
                  </div>
                )}

                {capa.ssem_dimension && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">SSEM Dimension</label>
                    <p className="text-gray-900 mt-1">{capa.ssem_dimension}</p>
                  </div>
                )}

                {/* Approval Information */}
                {(capa.approved_by || capa.rejected_by || capa.submitted_by) && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-gray-600 mb-3">Approval Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {capa.submitted_at && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600">Submitted At</label>
                          <p className="text-gray-900">{formatDate(capa.submitted_at)}</p>
                        </div>
                      )}
                      {capa.approved_at && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600">Approved At</label>
                          <p className="text-gray-900">{formatDate(capa.approved_at)}</p>
                        </div>
                      )}
                      {capa.approval_comments && (
                        <div className="col-span-2">
                          <label className="text-sm font-semibold text-gray-600">Approval Comments</label>
                          <p className="text-gray-900 mt-1">{capa.approval_comments}</p>
                        </div>
                      )}
                      {capa.rejection_reason && (
                        <div className="col-span-2">
                          <label className="text-sm font-semibold text-gray-600">Rejection Reason</label>
                          <p className="text-red-700 mt-1">{capa.rejection_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* S2-24: evidence attachments */}
        <Card>
          <CardHeader>
            <CardTitle>Evidence</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {capa.evidence_required && evidenceFiles.length === 0 && (
              <Alert>
                <AlertDescription>
                  This CAPA requires evidence. Attach at least one file before submitting it for verification or closing it.
                </AlertDescription>
              </Alert>
            )}
            <FileUploadWidget
              module="capa"
              referenceId={capa.id}
              maxFiles={10}
              maxSizeMB={10}
              allowedExtensions={['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg']}
              existingFiles={evidenceFiles}
              onFilesChange={handleEvidenceChange}
              disabled={capa.status === 'Closed' || !hasPermission('capa', 'update')}
            />
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Status Change Buttons */}
              {(() => {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const userId = user.user_id || user.id;
                const isAssignedUser = capa.assigned_to === userId;
                const canWork = isAssignedUser || hasPermission('capa', 'update');
                const canApprove = hasPermission('capa', 'approve');
                // CMP-08: the server refuses approval by the creator or the
                // assignee (segregation of duties); don't offer the button.
                const isCreatorOrAssignee = isAssignedUser || capa.created_by === userId;
                const canApproveThis = canApprove && !isCreatorOrAssignee;

                if (!canWork && !canApprove) {
                  return (
                    <Alert>
                      <AlertDescription>
                        Only the assigned user ({capa.assigned_to_name}) or users with appropriate permissions can take actions on this CAPA.
                      </AlertDescription>
                    </Alert>
                  );
                }

                return (
                  <div className="flex flex-wrap gap-3">
                    {capa.status === 'Draft' && canWork && (
                      <Button
                        onClick={handleSubmitForApproval}
                        disabled={actionLoading}
                        variant="default"
                      >
                        {actionLoading ? 'Submitting...' : 'Submit for Approval'}
                      </Button>
                    )}

                    {capa.status === 'Pending Approval' && canApprove && !canApproveThis && (
                      <p className="text-sm text-gray-600">
                        This CAPA is waiting for approval by someone other than its creator or assignee.
                      </p>
                    )}

                    {capa.status === 'Pending Approval' && canApproveThis && (
                      <>
                        <Button
                          onClick={() => setShowApproveModal(true)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => setShowRejectModal(true)}
                          disabled={actionLoading}
                          variant="destructive"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    )}

                    {capa.status === 'Assigned' && canWork && (
                      <Button
                        onClick={() => handleStatusChange('In Progress')}
                        disabled={actionLoading}
                        variant="default"
                      >
                        Start Working
                      </Button>
                    )}

                    {capa.status === 'In Progress' && canWork && (
                      <Button
                        onClick={() => handleStatusChange('Pending Verification')}
                        disabled={actionLoading}
                        variant="default"
                      >
                        Submit for Verification
                      </Button>
                    )}

                    {capa.status === 'Pending Verification' && canApprove && (
                      <>
                        <Button
                          onClick={() => setShowVerifyModal(true)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Verify CAPA
                        </Button>
                        <Button
                          onClick={() => setShowRejectModal(true)}
                          disabled={actionLoading}
                          variant="destructive"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject CAPA
                        </Button>
                      </>
                    )}

                    {capa.status === 'Verified' && canApprove && (
                      <Button
                        onClick={handleCloseCapa}
                        disabled={actionLoading}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {actionLoading ? 'Closing...' : 'Close CAPA'}
                      </Button>
                    )}

                    {capa.status === 'Rejected' && canWork && (
                      <Button
                        onClick={() => handleStatusChange('In Progress')}
                        disabled={actionLoading}
                        variant="default"
                      >
                        Resume Work
                      </Button>
                    )}
                  </div>
                );
              })()}

              {/* Current Status Display */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  Current Status: <span className={`font-semibold ${getStatusColor(capa.status)}`}>{capa.status}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
          </>
        ) : (
          /* Audit History Tab */
          <Card>
            <CardContent className="pt-6">
              <AuditTrailViewer
                entityType="capa"
                entityId={id}
                title="CAPA Audit History"
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Verify CAPA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-gray-700 mb-1">
                  Was the action effective?
                </legend>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="capa-effectiveness"
                    value="effective"
                    checked={isEffective}
                    onChange={() => setIsEffective(true)}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">Effective</span>
                    <span className="block text-gray-500">The CAPA will be closed.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="capa-effectiveness"
                    value="not_effective"
                    checked={!isEffective}
                    onChange={() => setIsEffective(false)}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">Not effective</span>
                    <span className="block text-gray-500">The CAPA goes back to In Progress for more work.</span>
                  </span>
                </label>
              </fieldset>
              <p className="text-sm text-gray-600">
                Please provide verification comments:
              </p>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter verification comments..."
              />
              <div className="flex gap-3">
                <Button
                  onClick={handleVerify}
                  disabled={actionLoading}
                  className={`flex-1 ${isEffective ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                >
                  {actionLoading
                    ? 'Saving...'
                    : (isEffective ? 'Verify and Close' : 'Record Not Effective')}
                </Button>
                <Button
                  onClick={() => {
                    setShowVerifyModal(false);
                    setComments('');
                    setIsEffective(true);
                  }}
                  disabled={actionLoading}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Approve CAPA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide approval comments (optional):
              </p>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter approval comments..."
              />
              <div className="flex gap-3">
                <Button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {actionLoading ? 'Approving...' : 'Approve'}
                </Button>
                <Button
                  onClick={() => {
                    setShowApproveModal(false);
                    setApprovalComments('');
                  }}
                  disabled={actionLoading}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Reject CAPA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide reason for rejection:
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter rejection reason..."
              />
              <div className="flex gap-3">
                <Button 
                  onClick={handleReject}
                  disabled={actionLoading}
                  variant="destructive"
                  className="flex-1"
                >
                  {actionLoading ? 'Rejecting...' : 'Reject'}
                </Button>
                <Button 
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  disabled={actionLoading}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </div>
  );
};

export default CAPADetail;
